import React, { useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import { InputContainer, Container, Error, Label, PasswordToggle, SubLabel } from './styles';
import { validateCompanyLegalName } from '../../../utils/fieldsValidation';

const fullNameRegex = new RegExp(
  /^[A-Za-zÀ-ü]{2,}[\s]([A-Za-zÀ-ü']{2,}|[A-Za-zÀ-ü.']{2,}[\s]|[A-Za-zÀ-ü']{1,}[\s])[A-Za-zÀ-ü']*/,
  'gi',
);
const schema = Yup.object().shape({
  name: Yup.string()
    .required('Insira nome e sobrenome')
    .matches(fullNameRegex, {
      message: 'O nome e/ou o sobrenome precisam ter 2 caracteres ou mais',
    })
    .matches(/^([^0-9]*)$/, {
      message: 'O nome não pode conter números',
    })
    .required('Nome obrigatório')
    .max(40, 'Este campo tem limite de 40 caracteres'),
  cpf: Yup.string().required('Cpf obrigatório'),
  email: Yup.string().required('Email obrigatório').email('Email inválido'),
  companyLegalName: Yup.string()
    .required('Razão Social obrigatória')
    .test('companyLegalName', 'Razão Social inválida. Use apenas caracteres permitidos.', (value) => {
      return !validateCompanyLegalName(value || '');
    }),
});

interface InputData {
  name?: any;
  value?: any;
  icon?: any;
  isDisabled?: boolean;
  label?: string;
  containerType?: any;
  norMarginLeft?: boolean;
  validator?: any;
  onBlur?: any;
  subLabel?: string;
  onChange?: any;
  hasError?: any;
  type?: string;
  readonly?: boolean;
  isPassword?: boolean;
}

type InputProps = JSX.IntrinsicElements['input'] & InputData;

const Input: React.FC<InputProps> = ({
  name,
  icon: Icon,
  isDisabled,
  label,
  subLabel,
  containerType,
  norMarginLeft,
  validator,
  onBlur: onBlurProps,
  onChange: onChangeProps,
  hasError,
  type = 'text',
  readonly = false,
  isPassword = false,
  ...rest
}) => {
  const {
    register,
    formState: { errors },
    trigger,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (event: any) => {
    type === 'number' && (event.target.value = parseInt(event.target.value));
    event.stopPropagation();
    if (onChangeProps) onChangeProps(event.target);
  };

  const handleBlur = (event: any) => {
    event.persist();
    if (onBlurProps) onBlurProps(event);
    if (!readonly) trigger(name);
    setValue(name, event.target.value);
    setIsFocused(false);
  };

  useEffect(() => {
    if (!hasError) return;
    hasError(name, !!errors[name]);
  }, [errors[name]]);

  return (
    <Container containerType={containerType}>
      <Label htmlFor={name} {...{ isFocused: isFocused, isDisabled: isDisabled }}>
        {label}
      </Label>

      {!!subLabel && <SubLabel>{subLabel}</SubLabel>}

      <InputContainer {...{ isErrored: !!errors[name], isFocused: !!isFocused }}>
        {errors[name] && <Error className="error-form">{errors[name]?.message?.toString()}</Error>}
        <input
          autoComplete="nope"
          {...register(name)}
          name={name}
          // onFocus={handleInputFocus}
          // defaultValue={defaultValue}
          onBlur={handleBlur}
          onChange={handleChange}
          type={isPassword ? (showPassword ? 'text' : 'password') : type}
          readOnly={readonly}
          style={isPassword ? { paddingRight: '48px' } : {}}
          {...rest}
        />
        {isPassword ? (
          <PasswordToggle type="button" onClick={() => setShowPassword((prevState) => !prevState)}>
            {showPassword ? (
              <svg width="20" height="30" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M13.6134 5.98268C12.2667 2.85602 9.73338 0.916016 7.00004 0.916016C4.26671 0.916016 1.73338 2.85602 0.386711 5.98268C0.350002 6.06679 0.331055 6.15758 0.331055 6.24935C0.331055 6.34112 0.350002 6.43191 0.386711 6.51602C1.73338 9.64268 4.26671 11.5827 7.00004 11.5827C9.73338 11.5827 12.2667 9.64268 13.6134 6.51602C13.6501 6.43191 13.669 6.34112 13.669 6.24935C13.669 6.15758 13.6501 6.06679 13.6134 5.98268ZM7.00004 10.2493C4.88671 10.2493 2.88671 8.72268 1.73338 6.24935C2.88671 3.77602 4.88671 2.24935 7.00004 2.24935C9.11338 2.24935 11.1134 3.77602 12.2667 6.24935C11.1134 8.72268 9.11338 10.2493 7.00004 10.2493ZM7.00004 3.58268C6.47263 3.58268 5.95706 3.73908 5.51852 4.0321C5.07999 4.32511 4.7382 4.74159 4.53637 5.22886C4.33453 5.71613 4.28172 6.25231 4.38462 6.76959C4.48751 7.28687 4.74149 7.76203 5.11443 8.13497C5.48737 8.50791 5.96252 8.76188 6.4798 8.86478C6.99709 8.96767 7.53326 8.91486 8.02053 8.71303C8.5078 8.5112 8.92428 8.1694 9.2173 7.73087C9.51031 7.29234 9.66671 6.77677 9.66671 6.24935C9.66671 5.54211 9.38576 4.86383 8.88566 4.36373C8.38556 3.86363 7.70729 3.58268 7.00004 3.58268ZM7.00004 7.58268C6.73634 7.58268 6.47855 7.50448 6.25928 7.35798C6.04002 7.21147 5.86912 7.00323 5.7682 6.75959C5.66729 6.51596 5.64088 6.24787 5.69233 5.98923C5.74378 5.73059 5.87077 5.49301 6.05724 5.30654C6.24371 5.12007 6.48128 4.99308 6.73992 4.94164C6.99857 4.89019 7.26665 4.91659 7.51029 5.01751C7.75392 5.11843 7.96216 5.28932 8.10867 5.50859C8.25518 5.72785 8.33338 5.98564 8.33338 6.24935C8.33338 6.60297 8.1929 6.94211 7.94285 7.19216C7.6928 7.44221 7.35367 7.58268 7.00004 7.58268Z"
                  fill="#101010"
                />
              </svg>
            ) : (
              <svg width="21" height="31" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M7.29378 4.80329C7.52762 4.76745 7.76388 4.74962 8.00045 4.74996C10.1204 4.74996 12.1138 6.27663 13.2738 8.74996C13.0964 9.12636 12.896 9.49149 12.6738 9.84329C12.6032 9.9525 12.5662 10.08 12.5671 10.21C12.5686 10.3555 12.6176 10.4965 12.7068 10.6115C12.7959 10.7265 12.9202 10.8092 13.0607 10.847C13.2012 10.8847 13.3502 10.8755 13.485 10.8207C13.6197 10.7658 13.7329 10.6684 13.8071 10.5433C14.1177 10.0552 14.3875 9.54239 14.6138 9.00996C14.6495 8.92677 14.668 8.83718 14.668 8.74663C14.668 8.65608 14.6495 8.56648 14.6138 8.48329C13.2671 5.35663 10.7338 3.41663 8.00045 3.41663C7.68756 3.41505 7.37517 3.44183 7.06712 3.49663C6.97957 3.51151 6.89581 3.54349 6.82062 3.59075C6.74543 3.638 6.68029 3.6996 6.62891 3.77203C6.57752 3.84446 6.54091 3.9263 6.52116 4.01288C6.50141 4.09946 6.4989 4.18908 6.51378 4.27663C6.52867 4.36418 6.56065 4.44793 6.6079 4.52312C6.65515 4.59831 6.71675 4.66346 6.78918 4.71484C6.86161 4.76622 6.94345 4.80283 7.03003 4.82258C7.11661 4.84234 7.20623 4.84484 7.29378 4.82996V4.80329ZM2.47378 2.27663C2.41162 2.21447 2.33783 2.16516 2.25662 2.13152C2.1754 2.09788 2.08836 2.08057 2.00045 2.08057C1.91254 2.08057 1.8255 2.09788 1.74428 2.13152C1.66307 2.16516 1.58927 2.21447 1.52712 2.27663C1.40158 2.40216 1.33105 2.57243 1.33105 2.74996C1.33105 2.9275 1.40158 3.09776 1.52712 3.22329L3.59378 5.28329C2.65083 6.19098 1.90035 7.27928 1.38712 8.48329C1.35041 8.5674 1.33146 8.65819 1.33146 8.74996C1.33146 8.84173 1.35041 8.93252 1.38712 9.01663C2.73378 12.1433 5.26712 14.0833 8.00045 14.0833C9.19852 14.075 10.3683 13.7183 11.3671 13.0566L13.5271 15.2233C13.5891 15.2858 13.6628 15.3354 13.7441 15.3692C13.8253 15.4031 13.9124 15.4205 14.0004 15.4205C14.0885 15.4205 14.1756 15.4031 14.2568 15.3692C14.3381 15.3354 14.4118 15.2858 14.4738 15.2233C14.5363 15.1613 14.5859 15.0876 14.6197 15.0063C14.6536 14.9251 14.671 14.838 14.671 14.75C14.671 14.662 14.6536 14.5748 14.6197 14.4936C14.5859 14.4123 14.5363 14.3386 14.4738 14.2766L2.47378 2.27663ZM6.71378 8.40329L8.34712 10.0366C8.23444 10.0689 8.11766 10.0847 8.00045 10.0833C7.64683 10.0833 7.30769 9.94282 7.05764 9.69277C6.80759 9.44272 6.66712 9.10358 6.66712 8.74996C6.66575 8.63275 6.68147 8.51597 6.71378 8.40329ZM8.00045 12.75C5.88045 12.75 3.88712 11.2233 2.73378 8.74996C3.16451 7.79913 3.77583 6.94101 4.53378 6.22329L5.71378 7.41663C5.43661 7.92249 5.33094 8.50459 5.41262 9.0756C5.4943 9.64661 5.75893 10.1757 6.1668 10.5836C6.57467 10.9915 7.1038 11.2561 7.67481 11.3378C8.24582 11.4195 8.82792 11.3138 9.33378 11.0366L10.3938 12.0833C9.66784 12.5105 8.84273 12.7404 8.00045 12.75Z"
                  fill="#101010"
                />
              </svg>
            )}
          </PasswordToggle>
        ) : null}
        {/* {error} */}
        {/* {!error && isFilled && !isFocused && containerType !== 'hidden' && (
          <img src={checkIconBlack} alt="Check icon" />
        )}
        {error && <img src={errorIcon} alt="Error icon" />} */}
      </InputContainer>
    </Container>
  );
};

export default Input;
